/**
 * App-side adapter between the generic `@paradigm/terminal-sync` client and
 * this app's terminal storage (`chat-history-storage.ts`).
 *
 * The desktop's gateway deployment runs the session-sync service in token
 * mode (no Paradex JWT chain on Paradigm boxes — see
 * api/paradex/terminal/SESSION-PERSISTENCE.md): requests authenticate with
 * the same gateway/pairing token the app already holds (and persists in
 * server-side preferences, so it survives sign-outs), plus an
 * `X-Sync-User` header to namespace storage on the single-tenant box.
 *
 * localStorage remains the synchronous cache the UI paints from;
 * the gateway copy is what survives sign-outs and device switches.
 */

import {
  syncBaseUrlFromAgentUrl,
  TerminalSyncClient,
} from '@paradigm/terminal-sync/src/client';
import {
  emptyIndexDoc,
  mergeIndex,
  mergeMessages,
} from '@paradigm/terminal-sync/src/merge';

import { clearAgentSession, getAgentToken, getAgentUrl } from './agent-url';
import {
  clearChatHistory,
  deleteSessionMessages,
  loadCustomFolders,
  loadHistoryUiState,
  loadPendingCleanups,
  loadSessionMessages,
  loadSessions,
  loadTombstones,
  PENDING_CLEANUP_MAX_AGE_MS,
  removePendingCleanup,
  saveCustomFolders,
  saveHistoryUiState,
  saveSessionMessages,
  saveSessions,
  saveTombstones,
  updatePendingCleanup,
} from './chat-history-storage';

import type { PendingCleanup } from './chat-history-storage';

import type {
  SyncDocRef,
  SyncIndexDoc,
  SyncMessagesDoc,
} from '@paradigm/terminal-sync/src/types';
import type { ChatMessage, ChatSession, CustomFolder } from './types';

/**
 * LaunchDarkly flag gating terminal session persistence to the user's
 * gateway instance. Fail closed: with the flag off the terminal behaves
 * exactly as before (localStorage only).
 */
export const FEATURE_TERMINAL_SESSION_SYNC = 'feature_terminal_session_sync';

export type TerminalIndexDoc = SyncIndexDoc<ChatSession, CustomFolder>;
export type TerminalMessagesDoc = SyncMessagesDoc<ChatMessage>;
export type TerminalSync = TerminalSyncClient<
  ChatMessage,
  ChatSession,
  CustomFolder
>;

/**
 * Build the index document for upload: persisted sessions (temporary chats
 * never leave the device), folders, and the locally recorded tombstones.
 */
export function buildIndexDoc(
  userId: string,
  sessions: readonly ChatSession[],
  folders: readonly CustomFolder[],
): TerminalIndexDoc {
  return {
    sessions: sessions.filter((s) => s.isTemporary !== true),
    folders,
    deleted: loadTombstones(userId),
  };
}

function asIndexDoc(value: unknown): TerminalIndexDoc {
  const v = (value ?? {}) as Partial<TerminalIndexDoc>;
  return {
    sessions: Array.isArray(v.sessions) ? v.sessions : [],
    folders: Array.isArray(v.folders) ? v.folders : [],
    deleted: Array.isArray(v.deleted) ? v.deleted : [],
  };
}

function asMessagesDoc(value: unknown): TerminalMessagesDoc {
  const v = (value ?? {}) as Partial<TerminalMessagesDoc>;
  return { messages: Array.isArray(v.messages) ? v.messages : [] };
}

function resolveTerminalConflict(
  ref: SyncDocRef,
  local: unknown,
  remote: unknown,
): unknown {
  if (ref.kind === 'index') {
    return mergeIndex(asIndexDoc(local), asIndexDoc(remote));
  }
  if (ref.kind === 'session') {
    return {
      messages: mergeMessages(
        asMessagesDoc(local).messages,
        asMessagesDoc(remote).messages,
      ),
    };
  }
  // ui-state: pure UI preference — last writer wins, and the local copy is
  // what the user is looking at right now.
  return local;
}

function persistMergedDoc(
  userId: string,
  ref: SyncDocRef,
  merged: unknown,
): void {
  if (ref.kind === 'index') {
    const doc = asIndexDoc(merged);
    saveSessions(userId, doc.sessions);
    saveCustomFolders(userId, doc.folders);
    saveTombstones(userId, doc.deleted);
    return;
  }
  if (ref.kind === 'session') {
    saveSessionMessages(userId, ref.sessionId, asMessagesDoc(merged).messages);
  }
  // ui-state: nothing to persist locally beyond what the app already saved.
}

/**
 * Create a sync client for this user, or null when no usable gateway URL
 * is known yet. The caller owns the lifecycle (`dispose()` on user switch
 * or unmount).
 *
 * `getFallbackToken` supplies the preferences-persisted pairing token for
 * the window between sign-in and the localStorage token mirror.
 */
export function createTerminalSync(
  userId: string,
  agentUrl?: string | null,
  getFallbackToken?: () => string | null,
): TerminalSync | null {
  const baseUrl = syncBaseUrlFromAgentUrl(agentUrl ?? getAgentUrl(userId));
  if (baseUrl == null) return null;
  return new TerminalSyncClient<ChatMessage, ChatSession, CustomFolder>({
    baseUrl,
    getAuthHeaders: () => {
      const token = getAgentToken(userId) ?? getFallbackToken?.() ?? null;
      if (token == null || token.length === 0) return null;
      return {
        Authorization: `Bearer ${token}`,
        'X-Sync-User': userId.toLowerCase(),
      };
    },
    resolveConflict: resolveTerminalConflict,
    onMerged: (ref, merged) => persistMergedDoc(userId, ref, merged),
  });
}

/**
 * Lazy-load a transcript from the gateway (used when switching to a session
 * whose messages aren't cached locally — e.g. it was created on another
 * device). Merges with whatever is local, persists, and returns the merged
 * transcript; null when there's nothing remote or the gateway is offline.
 */
export async function fetchAndMergeSessionFromRemote(
  userId: string,
  client: TerminalSync,
  sessionId: string,
): Promise<readonly ChatMessage[] | null> {
  const envelope = await client.fetchSession(sessionId);
  if (envelope == null) return null;
  const remote = asMessagesDoc(envelope.doc).messages;
  if (remote.length === 0) return null;
  const merged = mergeMessages(loadSessionMessages(userId, sessionId), remote);
  saveSessionMessages(userId, sessionId, merged);
  return merged;
}

export interface RemoteHydrationResult {
  /** Merged sessions index (already persisted to localStorage). */
  readonly sessions: readonly ChatSession[];
  /** Merged custom folders (already persisted). */
  readonly folders: readonly CustomFolder[];
  /** Session ids whose transcripts changed locally during the merge. */
  readonly updatedSessionIds: ReadonlySet<string>;
}

/**
 * Merge the server snapshot into local storage and queue uploads for
 * anything that exists only locally. Returns null when the gateway is
 * unreachable (caller continues local-only — never blocks the UI).
 *
 * Data-safety invariants:
 *  - merges are unions — neither side's sessions or messages are dropped;
 *  - tombstones (local or remote) remove transcripts on both sides;
 *  - local-only history (including all pre-sync history, i.e. the
 *    migration path) is uploaded, not replaced.
 */
export async function hydrateFromRemote(
  userId: string,
  client: TerminalSync,
): Promise<RemoteHydrationResult | null> {
  const snapshot = await client.fetchSnapshot();
  if (snapshot == null) return null;

  const localIndex = buildIndexDoc(
    userId,
    loadSessions(userId),
    loadCustomFolders(userId),
  );
  const remoteIndex =
    snapshot.index == null
      ? emptyIndexDoc<ChatSession, CustomFolder>()
      : asIndexDoc(snapshot.index.doc);
  const merged = mergeIndex(localIndex, remoteIndex);
  saveSessions(userId, merged.sessions);
  saveCustomFolders(userId, merged.folders);
  saveTombstones(userId, merged.deleted);

  // Apply remote ui-state when the local copy is the unmodified default —
  // i.e. a fresh device. Without this, the queueUiState() below would
  // upload that default and overwrite the server copy on every sign-in
  // from a new device, making ui-state effectively upload-only.
  const localUiState = loadHistoryUiState(userId);
  const localUiIsDefault =
    localUiState.activeSessionId == null &&
    localUiState.collapsed.paradex === false &&
    localUiState.collapsed.paradigm === false;
  if (localUiIsDefault && snapshot.uiState != null) {
    const remoteUi = snapshot.uiState.doc as Partial<typeof localUiState>;
    saveHistoryUiState(userId, {
      activeSessionId:
        typeof remoteUi.activeSessionId === 'string'
          ? remoteUi.activeSessionId
          : null,
      collapsed: {
        paradex: remoteUi.collapsed?.paradex === true,
        paradigm: remoteUi.collapsed?.paradigm === true,
      },
    });
  }

  const liveIds = new Set(merged.sessions.map((s) => s.sessionId));
  // Hoisted: also used by the tombstone propagation loop below.
  const remoteIds = new Set(snapshot.sessionList.map((item) => item.id));
  const clearedAtById = new Map(
    merged.sessions.map((s) => [s.sessionId, s.clearedAt ?? 0] as const),
  );
  const updatedSessionIds = new Set<string>();

  // Merge the transcripts included in the snapshot into the local blobs.
  // Drop any message older than the session's `clearedAt`, since the
  // user explicitly wiped it on some device.
  for (const [sessionId, envelope] of Object.entries(snapshot.sessions)) {
    if (!liveIds.has(sessionId)) continue; // tombstoned — skip
    const clearedAt = clearedAtById.get(sessionId) ?? 0;
    const local = loadSessionMessages(userId, sessionId).filter(
      (m) => m.timestamp > clearedAt,
    );
    const remote = asMessagesDoc(envelope.doc).messages.filter(
      (m) => m.timestamp > clearedAt,
    );
    const mergedMessages = mergeMessages(local, remote);
    if (mergedMessages.length !== local.length) {
      updatedSessionIds.add(sessionId);
    }
    saveSessionMessages(userId, sessionId, mergedMessages);
  }

  // Apply tombstones locally: drop transcripts for deleted sessions and
  // propagate the deletion to the server (idempotent). Skip the DELETE
  // when the server doesn't have the doc — otherwise we issue N round-
  // trips per launch for tombstones the server already knows about (TTL
  // is 90d, harmless 404 but pointless traffic).
  for (const tombstone of merged.deleted) {
    if (tombstone.kind !== 'session') continue;
    if (liveIds.has(tombstone.id)) continue; // resurrected by later activity
    deleteSessionMessages(userId, tombstone.id);
    if (remoteIds.has(tombstone.id)) {
      client.queueDeleteSession(tombstone.id);
    }
  }

  // Upload local-only transcripts — this is also the one-shot migration of
  // pre-sync history on the first hydration against an empty server.
  // Skip sessions whose transcript was explicitly cleared (the local
  // delete already propagated; re-uploading would resurrect them).
  for (const session of merged.sessions) {
    if (remoteIds.has(session.sessionId)) continue;
    const messages = loadSessionMessages(userId, session.sessionId);
    if (messages.length === 0) continue;
    client.queueSession(session.sessionId, { messages });
  }

  // Converge the index + ui-state.
  client.queueIndex(buildIndexDoc(userId, merged.sessions, merged.folders));
  client.queueUiState({ ...loadHistoryUiState(userId) });

  return {
    sessions: merged.sessions,
    folders: merged.folders,
    updatedSessionIds,
  };
}

// ───── Bounded sign-out retention cleanup ──────────────────────────────────

/**
 * Don't re-attempt the same entry more often than this. Cleans up a stuck
 * gateway scenario gracefully instead of spamming auth-rejected requests
 * every time the user navigates.
 */
const PENDING_CLEANUP_RETRY_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Attempt a final sync flush for a user who signed out while the gateway
 * was unreachable. Builds a temporary sync client using their persisted
 * URL + token, hydrates (which queues local-only data for upload), and
 * flushes. Returns whether the queue fully drained.
 *
 * Exported for the cleanup pass + unit tests.
 */
export async function attemptPendingCleanupFlush(
  userId: string,
): Promise<boolean> {
  // Sign out unconditionally invalidated the agent session token in the
  // pre-sync code path, but the new flow keeps it (alongside the chat
  // data we couldn't flush) precisely so this attempt has credentials.
  // getAgentToken(userId) reads from localStorage — null/empty means
  // there's no usable credential and the flush would 401 indefinitely.
  if (
    getAgentUrl(userId) == null ||
    getAgentUrl(userId)?.length === 0 ||
    getAgentToken(userId) == null ||
    getAgentToken(userId)?.length === 0
  ) {
    return false;
  }
  const sync = createTerminalSync(userId, null, () => getAgentToken(userId));
  if (sync == null) return false;
  try {
    const hydrated = await hydrateFromRemote(userId, sync);
    if (hydrated == null) {
      // Gateway unreachable / no auth — leave the entry for the next pass.
      return false;
    }
    return await sync.flush();
  } finally {
    sync.dispose();
  }
}

/**
 * Process all pending-cleanup entries other than the currently-signed-in
 * user's. Caller is responsible for already having removed `excludeUserId`
 * from the pending list (their data is no longer orphaned).
 *
 * For each remaining entry:
 *   - age > PENDING_CLEANUP_MAX_AGE_MS → hard-wipe, regardless of flush;
 *   - throttled by lastAttemptAt;
 *   - otherwise attempt a flush; wipe + remove entry on success.
 */
export async function runPendingCleanups(
  excludeUserId: string | null,
  now: number = Date.now(),
): Promise<void> {
  const excludeLower = excludeUserId?.toLowerCase() ?? null;
  const entries = loadPendingCleanups().filter(
    (c) => c.userId !== excludeLower,
  );
  for (const entry of entries) {
    try {
      await processCleanupEntry(entry, now);
    } catch (_err) {
      // Best-effort: a transient failure on one entry must not block the
      // others. The next sign-in will retry.
    }
  }
}

async function processCleanupEntry(
  entry: PendingCleanup,
  now: number,
): Promise<void> {
  // Guard against clock skew / corrupt entries: a signedOutAt in the
  // future or older than the epoch starts gets the same treatment as an
  // expired one — wipe and move on.
  const age =
    Number.isFinite(entry.signedOutAt) && entry.signedOutAt <= now
      ? now - entry.signedOutAt
      : Number.POSITIVE_INFINITY;
  if (age > PENDING_CLEANUP_MAX_AGE_MS) {
    clearAgentSession(entry.userId);
    clearChatHistory(entry.userId);
    removePendingCleanup(entry.userId);
    return;
  }
  const lastAttempt = entry.lastAttemptAt ?? 0;
  if (now - lastAttempt < PENDING_CLEANUP_RETRY_INTERVAL_MS) return;
  updatePendingCleanup(entry.userId, { lastAttemptAt: now });
  const ok = await attemptPendingCleanupFlush(entry.userId);
  if (ok) {
    clearAgentSession(entry.userId);
    clearChatHistory(entry.userId);
    removePendingCleanup(entry.userId);
  }
}
